import {
  ArticleContainer,
  ArticleEditButton,
  ArticleHeader,
  ArticleMeta,
  ArticleTitle,
  ArticleWrapper,
} from "./styles";
import "./article.css";
import React, { useState, useContext } from "react";
import { useQuery, gql } from "@apollo/client";
import ArticleContent from "./ArticleContent";
import { Redirect, useParams } from "react-router-dom";
import { AuthContext } from "../../../../AuthContext";
import WarningBanner from "../../../WarningBanner";
import { Helmet } from "react-helmet";
import { isContentWriter } from "../../../../AuthUtils";

const GET_ARTICLE_BY_ID_AND_SLUG = gql`
  query GetArticleByIdAndSlug($articleId: ID!, $slug: String!) {
    articleByIdAndSlug(id: $articleId, slug: $slug) {
      id
      title
      content
      authorized
      imageUrl
      author {
        username
        fullName
      }
      createdOn
      legacy

      slate {
        id
      }
    }

    whoAmI {
      groups {
        name
      }
    }
  }
`;

const GET_ARTICLE_BY_DATE_AND_SLUG = gql`
  query GetArticleByIdAndSlug(
    $day: Int!
    $month: Int!
    $year: Int!
    $slug: String!
  ) {
    articleByDateAndSlug(day: $day, month: $month, year: $year, slug: $slug) {
      id
      title
      content
      authorized
      imageUrl
      author {
        username
        fullName
      }
      createdOn
      legacy

      slate {
        id
      }
    }

    whoAmI {
      groups {
        name
      }
    }
  }
`;

function Article() {
  const { articleId, articleSlug, day, month, year } = useParams();

  const [playerTooltipLoc, setPlayerToolTipLoc] = useState(undefined);
  const [playerTooltipData, setPlayerToolTipData] = useState(undefined);

  // Following two conditionals support old ID based article lookup while also moving to new date based lookup
  const query = year
    ? GET_ARTICLE_BY_DATE_AND_SLUG
    : GET_ARTICLE_BY_ID_AND_SLUG;

  const queryVars = year
    ? { day: Number.parseInt(day), month: Number.parseInt(month), year: Number.parseInt(year), slug: articleSlug }
    : {
        articleId: articleId,
        slug: articleSlug,
      };

  const { loading, error, data } = useQuery(query, {
    variables: queryVars,
  });

  if (loading) return <></>;

  const article = year ? data.articleByDateAndSlug : data.articleByIdAndSlug;

  if (!article.authorized) return <Redirect to="/memberships" />;

  function handlePlayerMentionClick(e) {
    // `target` is the element the click was on (the div we hooked or an element
    // with in it), `currentTarget` is the div we hooked the event on
    let el = e.target;

    while (
      el &&
      el !== e.currentTarget &&
      el.className !== "article-player-mention" &&
      el.id !== "article-player-tooltip"
    ) {
      el = el.parentNode;
    }

    if (el && el.className === "article-player-mention") {
      const playerId = el.dataset.playerId;

      setPlayerToolTipLoc(el.getBoundingClientRect());

      setPlayerToolTipData({ id: playerId, value: el.textContent });
    } else if (el.id !== "article-player-tooltip") {
      setPlayerToolTipLoc(undefined);
      setPlayerToolTipData(undefined);
    }
  }

  const articleQueryId = article.id;
  const articleTitle = article.title;
  const articleContent = article.content;
  const articlePostedDate = article.createdOn;
  const articleAuthor = article.author.fullName;
  const articleIsLegacy = article.legacy;
  const articleSlate = article.slate;

  return (
    <>
      <Helmet>
        <title>{articleTitle}</title>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@GupsCorner" />
        <meta name="twitter:title" content={articleTitle} />
        <meta
          name="twitter:description"
          content="Friends. Family. Greenscreens."
        />
        <meta name="twitter:image" content={article.imageUrl} />
      </Helmet>
      {articleIsLegacy ? (
        <WarningBanner active={true}>
          This article was written on an older version of gupscorner.com. Some
          formatting, links, and files may not work as originally intended.
        </WarningBanner>
      ) : null}
      <ArticleContainer>
        <ArticleWrapper>
          <ArticleHeader>
            {isContentWriter(data.whoAmI ? data.whoAmI.groups : null) ? (
              <ArticleEditButton
                href={`/article/edit/${articleQueryId}`}
              >
                Edit
              </ArticleEditButton>
            ) : null}
            <ArticleTitle>{articleTitle}</ArticleTitle>
            <ArticleMeta>
              <span className="posted-on">Posted on {articlePostedDate}</span>
              <span>&nbsp;by&nbsp;</span>
              <span>{articleAuthor}</span>
            </ArticleMeta>
          </ArticleHeader>
          <ArticleContent
            articleContent={articleContent}
            articleSlateId={articleSlate ? articleSlate.id : null}
            playerTooltipLoc={playerTooltipLoc}
            playerTooltipData={playerTooltipData}
            onClick={handlePlayerMentionClick}
          />
        </ArticleWrapper>
      </ArticleContainer>
    </>
  );
}

export default Article;
